import { Link } from 'react-router-dom';
import {
  StatCard, Card, CardHeader, Table, THead, TBody, TR, TH, TD,
  StatusBadge, EmptyState, ErrorNotice, Loading, Button,
} from '../../components/ui/index.js';
import { OnboardingChecklist } from '../../components/OnboardingChecklist.jsx';
import { PageHero } from '../../components/PageHero.jsx';
import { useResource } from '../../hooks/useApi.js';
import { formatMoney, formatNumber, formatPercent, formatTimestamp, truncateId } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { LayoutDashboard } from 'lucide-react';

/** A dependency-free trend line: a chart library is not worth 40kB for this. */
function VolumeTrend({ points, currency }) {
  if (!points?.length) {
    return <p className="px-5 py-10 text-center text-sm text-muted">No settled payments in this period yet.</p>;
  }

  const width = 720;
  const height = 160;
  const max = Math.max(...points.map((p) => p.volume), 1);
  const step = points.length > 1 ? width / (points.length - 1) : width;

  const coords = points.map((p, i) => [i * step, height - (p.volume / max) * (height - 16) - 8]);
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;

  return (
    <div className="px-5 py-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-40 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Daily settled volume. Peak ${formatMoney(max, currency)}.`}
      >
        <defs>
          <linearGradient id="volume-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgb(52 97 245)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="rgb(52 97 245)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#volume-fill)" />
        <path d={line} fill="none" stroke="rgb(31 65 234)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-2 flex justify-between text-[11px] text-muted">
        <span>{points[0]?.date}</span>
        <span>Peak {formatMoney(max, currency)}</span>
        <span>{points[points.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export function OverviewPage() {
  const { merchant } = useAuth();
  const currency = merchant?.defaultCurrency ?? 'USD';

  // DB-5: fetched fresh on every mount. No client-side cache of money figures.
  const stats = useResource('/dashboard/overview');
  const recent = useResource('/dashboard/payments', { params: { limit: 5 } });

  return (
    <>
      <PageHero
        icon={LayoutDashboard}
        chip="Workspace"
        title={`Welcome back, ${merchant?.name ?? 'there'}`}
        blurb="Everything that moved money on your account, over the last 30 days."
        actions={<Link to="/transactions"><Button variant="secondary">View all transactions</Button></Link>}
      />

      {/* MA-6: the checklist stays on the dashboard until complete. */}
      <div className="mb-6"><OnboardingChecklist /></div>

      {stats.error ? (
        <ErrorNotice error={stats.error} onRetry={stats.refetch} title="Could not load your figures" className="mb-6" />
      ) : (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Net volume" loading={stats.loading}
            value={formatMoney(stats.data?.netVolume, currency)}
            hint={stats.data?.refundedVolume ? `${formatMoney(stats.data.refundedVolume, currency)} refunded` : 'After refunds'} />
          <StatCard label="Payments" loading={stats.loading} value={formatNumber(stats.data?.paymentCount)}
            hint={`${formatNumber(stats.data?.succeededCount ?? 0)} succeeded`} />
          <StatCard label="Success rate" loading={stats.loading}
            value={formatPercent(stats.data?.successRate)}
            // A rate of 100% from zero payments would be a lie; the API sends null.
            hint={stats.data?.successRate == null ? 'No payments yet' : 'Of all attempts'}
            tone={stats.data?.successRate != null && stats.data.successRate < 80 ? 'danger' : 'default'} />
          <StatCard label="Average payment" loading={stats.loading}
            value={formatMoney(stats.data?.averagePayment, currency)} hint="Per succeeded payment" />
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader title="Daily volume" description="Settled payments, net of refunds" />
          {stats.loading ? <Loading label="Loading volume…" /> : <VolumeTrend points={stats.data?.dailyVolume} currency={currency} />}
        </Card>

        <Card>
          <CardHeader
            title="Recent payments"
            actions={<Link to="/payments" className="text-xs font-medium text-accent-ink hover:underline">View all</Link>}
          />
          {/* FE-9: loading, error, empty and content, all handled explicitly. */}
          {recent.loading && <Loading label="Loading payments…" />}
          {recent.error && <ErrorNotice error={recent.error} onRetry={recent.refetch} className="m-4" />}
          {!recent.loading && !recent.error && recent.data?.payments?.length === 0 && (
            <EmptyState
              title="No payments yet"
              description="Payments you create through the API or the dashboard will appear here."
              action={<Link to="/payments"><Button size="sm">Create a payment</Button></Link>}
            />
          )}
          {!recent.loading && !recent.error && recent.data?.payments?.length > 0 && (
            <Table className="min-w-0">
              <THead><TR><TH>Payment</TH><TH align="right">Amount</TH><TH>Status</TH></TR></THead>
              <TBody>
                {recent.data.payments.map((payment) => (
                  <TR key={payment.id}>
                    <TD>
                      <Link to={`/payments/${payment.id}`} className="font-mono text-xs text-accent-ink hover:underline">
                        {truncateId(payment.id, 10, 4)}
                      </Link>
                      <div className="mt-0.5 text-[11px] text-muted">{formatTimestamp(payment.createdAt)}</div>
                    </TD>
                    <TD align="right" className="tabular whitespace-nowrap font-medium">
                      {formatMoney(payment.amount, payment.currency)}
                    </TD>
                    <TD><StatusBadge status={payment.status} size="sm" /></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

export default OverviewPage;
